import { isDefined } from '@sgme/fp';
import { WithInvalidFieldsContext } from 'components/common-form/contexts/WithInvalidFieldsContext';
import { CustodiansField } from 'components/secondaryMarket/shared';
import { Block } from 'components/secondaryMarket/shared/Blocks/Block';
import { TradeWayButtons } from 'components/secondaryMarket/shared/components/TradeWayButtons';
import {
  VerticalFormGroup,
  VerticalFormGroupContent,
  VerticalFormGroupLabel,
} from 'components/secondaryMarket/shared/VerticalFormGroup';
import { AmountWithUnit } from 'components/shared/Amount/Amount';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { DatePicker } from 'components/shared/DatePicker/DatePicker';
import { asIsoDate } from 'components/shared/DatePicker/utils';
import { NumberFormatter } from 'components/shared/NumberFormatter/NumberFormatter';
import { TimePicker } from 'components/shared/TimePicker';
import type { DlpSecondaryTradeInformationProps } from 'components/Workspaces/SecondaryMarketQuotes/Tabs/dlpOrder/dlpSecondaryTradeInformation.connect';
import { addDays } from 'date-fns';
import { SecondaryMarketKeys, SecondaryMarketKeysPrefix } from 'locales/secondaryMarketKeys';
import { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  allLimitValidities,
  allOrderTypes,
  type LimitValidity,
  type OrderType,
  SecondaryMarketDlpOrderForm,
} from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.model';
import { amountDefaultAccelerators } from 'utils/amount';

const fieldIds: string[] = ['quantity', 'custodian', 'limitDate', 'limitTime', 'limit'];

export function DlpSecondaryTradeInformation({
  onQuantityChanged,
  onTradeWayChanged,
  onCustodianChanged,
  onOrderTypeChanged,
  onValidityChanged,
  onLimitChanged,
  onLimitDateChanged,
  onLimitTimeChanged,
  tradeWay,
  orderType,
  selectedCustodian,
  availableCustodians,
  quantity,
  disabledTradeWays,
  validity,
  limit,
  limitDate,
  limitTime,
  isFrozen,
  maxQuantity,
  validationErrors,
}: DlpSecondaryTradeInformationProps) {
  const isLimit = orderType === 'Limit';
  const isGtd = validity === 'GTD';
  const onSelectOrderType = useCallback(
    (items: readonly OrderType[]) => {
      if (onOrderTypeChanged !== undefined) {
        onOrderTypeChanged(items[0]);
      }
    },
    [onOrderTypeChanged],
  );
  const onSelectValidity = useCallback(
    (items: readonly LimitValidity[]) => {
      if (onValidityChanged !== undefined) {
        onValidityChanged(items[0]);
      }
    },
    [onValidityChanged],
  );
  const onCustodianIdChanged = useCallback(
    (custodianId?: string) => {
      if (onCustodianChanged !== undefined) {
        onCustodianChanged(availableCustodians.find(c => c.id === custodianId));
      }
    },
    [onCustodianChanged, availableCustodians],
  );
  const onLimitDateValueChanged = useCallback(
    (date: string | null) => {
      onLimitDateChanged(date ? date : undefined);
    },
    [onLimitDateChanged],
  );

  useEffect(() => {
    const otherTradeWay: DlpSecondaryTradeInformationProps['tradeWay'] =
      tradeWay === 'ClientBuy' ? 'ClientSell' : 'ClientBuy';
    if (disabledTradeWays.includes(tradeWay) && !disabledTradeWays.includes(otherTradeWay)) {
      onTradeWayChanged(otherTradeWay);
    }
  }, [tradeWay, disabledTradeWays, onTradeWayChanged]);

  const limitDatevalues = useMemo(() => [limitDate], [limitDate]);

  const minDate = asIsoDate(addDays(new Date(), 1));
  const { formatMessage, formatNumber } = useIntl();
  const isQuantityDisabled = maxQuantity === 0;
  const quantityPlaceholder = isQuantityDisabled
    ? formatMessage({ id: 'secondaryMarket.tradeInformation.quantity.placeholder.zero' })
    : formatMessage(
        { id: 'secondaryMarket.tradeInformation.quantity.placeholder' },
        { maxQuantity: maxQuantity !== undefined ? formatNumber(maxQuantity) : '...' },
      );

  return (
    <WithInvalidFieldsContext<SecondaryMarketDlpOrderForm, SecondaryMarketKeys, SecondaryMarketKeysPrefix>
      validationErrors={validationErrors}
    >
      <Block label="secondaryMarket.tradeInformation.label.tradeInformation" id="trade-information" fieldIds={fieldIds}>
        <div className={isFrozen ? 'dlpOrder--frozen' : ''}>
          <VerticalFormGroup
            inputId="tradeWay"
            label="secondaryMarket.tradeInformation.tradeWay.label"
            fieldsClassName="row gy-1"
          >
            <TradeWayButtons
              value={tradeWay}
              onValueChanged={onTradeWayChanged}
              e2e="trade-way-buttons"
              className="col"
              disabledValues={disabledTradeWays}
              isDisabled={false}
            />
          </VerticalFormGroup>
          <VerticalFormGroup
            inputId="quantity"
            label="secondaryMarket.tradeInformation.quantity.label"
            fieldsClassName="row gy-1"
          >
            <div className="input-group">
              <NumberFormatter
                className="col"
                accelerators={amountDefaultAccelerators}
                onBlur={onQuantityChanged}
                e2e="quantity"
                incrementStep={1}
                value={quantity}
                precision={0}
                placeholder={quantityPlaceholder}
                isDisabled={isQuantityDisabled}
              />
            </div>
          </VerticalFormGroup>
          <VerticalFormGroup
            inputId="tradeWay"
            label="secondaryMarket.tradeInformation.orderType.label"
            fieldsClassName="row gy-1"
          >
            <ButtonsGroup
              className="col"
              items={allOrderTypes}
              selectedItems={orderType}
              onSelect={onSelectOrderType}
              isRadio
              translationPrefix="form.orderType"
              isDisabled={false}
              isReadonly={false}
              e2e="order-type-buttons"
            />
          </VerticalFormGroup>
          {isLimit && (
            <VerticalFormGroup
              inputId="tradeWay"
              label="secondaryMarket.tradeInformation.validity.label"
              fieldsClassName="row gy-1"
            >
              <ButtonsGroup
                className="col"
                items={allLimitValidities}
                selectedItems={isDefined(validity) ? validity : null}
                onSelect={onSelectValidity}
                isRadio
                translationPrefix="form.validity"
                isDisabled={false}
                isReadonly={false}
                e2e="validity-buttons"
              />
            </VerticalFormGroup>
          )}
          {isLimit && (
            <VerticalFormGroup
              inputId="limit"
              label="secondaryMarket.tradeInformation.limit.label"
              fieldsClassName="row gy-1"
            >
              <AmountWithUnit
                className="col"
                onBlur={onLimitChanged}
                e2e="limit"
                incrementStep={1}
                value={limit}
                precision={3}
              />
            </VerticalFormGroup>
          )}
          {isGtd && (
            <div className="mb-2">
              <VerticalFormGroupLabel inputId="limitDate" label={'secondaryMarket.tradeInformation.limitDate.label'} />
              <div className="row gy-1">
                <div className="col-8">
                  <VerticalFormGroupContent inputId="limitDate">
                    <DatePicker
                      onChange={onLimitDateValueChanged}
                      e2e="limit-date"
                      values={limitDatevalues}
                      minDate={minDate}
                      type="mono"
                    />
                  </VerticalFormGroupContent>
                </div>
                <div className="col-4">
                  <VerticalFormGroupContent inputId="limitTime">
                    <TimePicker onValueChanged={onLimitTimeChanged} value={limitTime} type="string" />
                  </VerticalFormGroupContent>
                </div>
                <div />
              </div>
            </div>
          )}
          <VerticalFormGroup
            inputId="custodian"
            label="secondaryMarket.tradeInformation.label.custodian"
            fieldsClassName="row gy-1"
          >
            <CustodiansField
              className="col mt-1"
              selectedBdrId={selectedCustodian?.id}
              custodians={availableCustodians}
              onChange={onCustodianIdChanged}
              readonly={availableCustodians.length <= 1}
              disabled={false}
            />
          </VerticalFormGroup>
        </div>
      </Block>
    </WithInvalidFieldsContext>
  );
}
